/**
 * @file Pipeline comum dos elementos baseados em `<input>` do EP.
 * @module components/create/input
 *
 * Este arquivo concentra o comportamento transversal dos inputs EP: defaults
 * de id/name/type, FieldBox, prefix/suffix, estados de validação e roteamento
 * para especializações como CEP, CPF, Phone, Date, Number e Password.
 *
 * @see module:components/create/element
 */

import cep from './cep';
import cnpj from './cnpj';
import cpf from './cpf';
import creditCard from './creditCard';
import date from './date';
import email from './email';
import fieldbox from './fieldbox';
import number from './number';
import password from './password';
import { slider } from './slider';
import spinner from './spinner';
import time from './time';
import url from './url';
import { applyFullDefault } from './fullDefaults';
import setIcon from '../define/setIcon';

/**
 * Implementação compartilhada dos inputs EP.
 *
 * @class
 */
class Input {
  counter = 0;

  /**
   * Grava metadado usando o helper `data()` quando disponível ou `dataset`.
   *
   * @param {HTMLElement} elem Elemento alvo.
   * @param {string} key Chave.
   * @param {*} value Valor.
   * @returns {void}
   */
  #setElemData(elem, key, value) {
    if (typeof elem?.data === 'function') {
      elem.data(key, value);
      return;
    }

    if (!elem?.dataset) return;
    elem.dataset[key] = value === false ? 'false' : String(value);
  }

  /**
   * Lê metadado pelo helper `data()` ou `dataset`.
   *
   * @param {HTMLElement} elem Elemento alvo.
   * @param {string} key Chave.
   * @returns {*} Valor armazenado.
   */
  #getElemData(elem, key) {
    if (typeof elem?.data === 'function') {
      return elem.data(key);
    }

    return elem?.dataset?.[key];
  }

  /**
   * Aplica objeto de estilos a um prefixo/sufixo.
   *
   * @param {HTMLElement} target Adornment.
   * @param {Object} style Estilos.
   * @returns {void}
   */
  #applyAdornmentStyles(target, style) {
    if (!style || typeof style !== 'object') return;
    Object.assign(target.style, style);
  }

  /**
   * Adiciona HTML, texto e/ou ícone ao prefixo/sufixo.
   *
   * `html` tem precedência e encerra a composição. Caso contrário, texto e
   * ícone podem coexistir.
   *
   * @param {HTMLElement} target Adornment.
   * @param {Object} [options={}] Configuração visual.
   * @returns {void}
   */
  #appendAdornmentContent(target, options = {}) {
    if (typeof options.html === 'string') {
      target.innerHTML = options.html;
      return;
    }

    if (typeof options.text === 'string') {
      const span = document.createElement('span');
      span.textContent = options.text;
      target.append(span);
    }

    if (options.icon) {
      setIcon(target, { icon: options.icon });
    }
  }

  /**
   * Define `mode: 'soft'` por padrão para adornments clicáveis.
   *
   * @param {*} options Configuração.
   * @returns {*} Configuração original ou cópia normalizada.
   */
  #normalizeAdornmentOptions(options) {
    if (!options || typeof options !== 'object') return options;

    if (typeof options.click !== 'function' || options.mode !== undefined) {
      return options;
    }

    return { ...options, mode: 'soft' };
  }

  /**
   * Implementação fallback de prefix/suffix quando o prototype helper não existe.
   *
   * @param {HTMLInputElement} elem Input.
   * @param {Object} options Configuração do adornment.
   * @param {'pfx'|'sfx'} type Lado/classe.
   * @returns {void}
   */
  #addAdornmentFallback(elem, options, type) {
    if (!options || !elem?.parentElement) return;

    const target = document.createElement(
      typeof options.click === 'function' ? 'button' : 'span'
    );

    if (target.tagName === 'BUTTON') {
      target.type = 'button';
      target.addEventListener('click', options.click);
    }

    target.className = options.class
      ? `${type} ${options.class}`
      : type;

    if (target.tagName === 'BUTTON' && options.mode) {
      target.classList.add(options.mode);
    }

    this.#applyAdornmentStyles(target, options.style);
    this.#appendAdornmentContent(target, options);

    if (type === 'pfx') {
      elem.before(target);
      return;
    }

    elem.after(target);
  }

  /**
   * Adiciona prefixo pelo prototype helper ou fallback local.
   *
   * @param {HTMLInputElement} elem Input.
   * @param {Object} options Configuração.
   * @returns {void}
   */
  #addPrefix(elem, options) {
    const normalizedOptions = this.#normalizeAdornmentOptions(options);

    if (typeof elem?.addPrefix === 'function') {
      elem.addPrefix(normalizedOptions);
      return;
    }

    this.#addAdornmentFallback(elem, normalizedOptions, 'pfx');
  }

  /**
   * Adiciona sufixo pelo prototype helper ou fallback local.
   *
   * @param {HTMLInputElement} elem Input.
   * @param {Object} options Configuração.
   * @returns {void}
   */
  #addSuffix(elem, options) {
    const normalizedOptions = this.#normalizeAdornmentOptions(options);

    if (typeof elem?.addSuffix === 'function') {
      elem.addSuffix(normalizedOptions);
      return;
    }

    this.#addAdornmentFallback(elem, normalizedOptions, 'sfx');
  }

  /**
   * Instala eventos comuns de foco e estado visual de validação.
   *
   * Campos vazios não ficam automaticamente inválidos após simples edição; o
   * estado usa `data-valid` quando validadores especializados o definem.
   *
   * @param {HTMLInputElement} elem Input.
   * @returns {HTMLInputElement} O mesmo input.
   */
  #addEvents(elem) {
    /**
     * Sincroniza classes `valid`/`invalid` do FieldBox.
     *
     * @param {HTMLInputElement} inputField Campo.
     * @param {boolean} [force=false] Considera o campo touched imediatamente.
     * @returns {void}
     */
    const updateValidationState = (inputField, force = false) => {
      const fieldbox = inputField.closest('.fieldbox');
      if (!fieldbox) return;
      const temporal = ['date', 'time'].includes(inputField.dataset.type);
      if (temporal) {
        inputField.validateDate?.();
        inputField.validateTime?.();
      }
      const touched = force || temporal || inputField.dataset.touched === 'true';
      const hasValue = (inputField.value || '').trim().length > 0;

      if (!touched) {
        fieldbox.classList.remove('invalid');
        fieldbox.classList.remove('valid');
        return;
      }

      if (!hasValue) {
        inputField.removeAttribute('data-valid');
        fieldbox.classList.remove('invalid');
        fieldbox.classList.remove('valid');
        return;
      }

      if (inputField.dataset.type === 'url') {
        inputField.checkUrl();
      } else if (inputField.validity.valid === false) {
        this.#setElemData(inputField, 'valid', false);
      } else if (['email', 'url'].includes(inputField.type)) {
        this.#setElemData(inputField, 'valid', true);
      }

      const state = this.#getElemData(inputField, 'valid');
      if (state === 'false') {
        fieldbox.classList.remove('valid');
        fieldbox.classList.add('invalid');
      } else if (state === 'true') {
        fieldbox.classList.remove('invalid');
        fieldbox.classList.toggle('valid', !temporal);
      } else {
        fieldbox.classList.remove('invalid');
        fieldbox.classList.remove('valid');
      }
    };

    // A máscara pode finalizar o texto depois dos listeners do Input.
    // Valida na microtask para considerar também colagem e seleção no calendário.
    for (const eventName of ['input', 'change']) {
      elem.addEventListener(eventName, () => {
        if (['date', 'time'].includes(elem.dataset.type)) {
          queueMicrotask(() => updateValidationState(elem));
        }
      });
    }
    if (['date', 'time'].includes(elem.dataset.type)) {
      requestAnimationFrame(() => updateValidationState(elem));
    }

    elem.addEventListener('keyup', (e) => {
      const inputField = e.target;
      updateValidationState(inputField);
    });

    elem.addEventListener('blur', (e) => {
      e.target.parentElement.classList.remove('focus');
      const inputField = e.target;
      inputField.dataset.touched = 'true';
      updateValidationState(inputField, true);
    });

    elem.addEventListener('focus', (e) => {
      if (e.target.required === false && e.target.disabled === false) {
        e.target.parentElement.classList.add('focus');
      }
    });

    return elem;
  }

  /**
   * Finaliza defaults, FieldBox, metadados, adornments e eventos do input.
   *
   * @param {HTMLInputElement} elem Input já especializado.
   * @param {Object} options Opções recebidas pelo pipeline.
   * @returns {Array.<HTMLInputElement|Object>} Input e o mesmo objeto `options` normalizado.
   * @see Input#input
   */
  #createInputOptions(elem, options) {
    options.tag = 'input';
    options = this.#setOptions(options);
    options.parent = fieldbox(options);
    this.#setElemData(elem, 'type', options.element);
    this.#addPrefix(elem, options.prefix);
    this.#addSuffix(elem, options.suffix);
    elem = this.#addEvents(elem);

    return [elem, options];
  }

  /**
   * Aplica defaults comuns do pipeline Input.
   *
   * Regras: `type` inválido vira `text`; parent é obrigatório; id/name são
   * gerados quando ausentes; label default não é criado para checkbox/hidden/
   * radio; flags booleanas aceitam `true` ou string vazia; `forcedValue` tem
   * precedência final; `full` é normalizado por `applyFullDefault()`.
   *
   * @param {Object} options Opções mutáveis recebidas pelo pipeline.
   * @returns {Object|undefined} A mesma referência normalizada.
   * @see Input#input
   */
  #setOptions(options) {
    options.tag = 'input';
    const typeList = ['checkbox', 'color', 'date', 'datetime-local', 'email', 'file', 'hidden', 'month', 'number', 'password', 'radio', 'range', 'search', 'tel', 'text', 'time', 'url', 'week'];

    if (options.type === undefined || typeList.indexOf(options.type) < 0) {
      options.type = 'text';
    }

    if (options.parent == undefined) {
      console.error(options);
      console.error('Falta o parent');
      return;
    }

    if (options.id === undefined) {
      options.id = `input-${++this.counter}`;
    }

    if (options.name === undefined) {
      options.name = options.id;
    }

    const noLabels = ['checkbox', 'hidden', 'radio'];
    if (options.label === undefined && !noLabels.includes(options.type)) {
      options.label = options.id;
    }

    if (options.readOnly !== undefined && (options.readOnly === true || options.readOnly === '')) {
      options.readOnly = true;
    } else {
      delete options.readOnly;
    }

    if (options.disabled !== undefined && (options.disabled === true || options.disabled === '')) {
      options.disabled = true;
    } else {
      delete options.disabled;
    }

    if (options.required !== undefined && (options.required === true || options.required === '')) {
      options.required = true;
    } else {
      delete options.required;
    }

    if (options.value === undefined && options.defaultValue !== undefined) {
      options.value = options.defaultValue;
    }

    if (options.forcedValue !== undefined) {
      options.value = options.forcedValue;
    }

    applyFullDefault(options);

    return options;
  }

  /**
   * Cria e especializa um input conforme `options.element`.
   *
   * Especializações roteadas atualmente: CEP, Checkbox, CNPJ, Color, CPF,
   * CreditCard, Currency, Date, Email, Hidden, Integer, Float, Password, Phone,
   * Radio, Slider, Spinner, Time e URL. Demais tipos caem em texto simples.
   *
   * @param {Object} options Configuração recebida pelo pipeline.
   * @param {string} options.element Tipo lógico EP, por exemplo `text`, `phone`, `cpf` ou `date`.
   * @param {string} [options.type='text'] Tipo HTML do `<input>` após normalização.
   * @param {*} [options.forcedValue] Quando informado, substitui `value` e `defaultValue`.
   * @param {'switch'|'button'|string} [options.appearance] Aparência de Checkbox/Radio.
   * @param {string} [options.text] Texto associado a Checkbox/Radio.
   * @returns {Promise.<Array.<HTMLInputElement|Object>>} Input e o mesmo objeto `options` normalizado.
   * @see module:components/create/element
   */
  async input(options) {
    let elem = document.createElement('input');

    switch (options.element) {
      case 'cep': [elem, options] = cep(elem, options); break;
      case 'checkbox':
        options.text = options.text || '';
        options.type = 'checkbox';
        switch (options.appearance) {
          case 'switch': options.class = 'switch'; break;
          case 'button': options.class = 'ch-button'; break;
        }
        break;
      case 'cnpj': [elem, options] = cnpj(elem, options); break;
      case 'color': options.type = 'color'; break;
      case 'cpf': [elem, options] = cpf(elem, options); break;
      case 'creditCard': [elem, options] = creditCard(elem, options); break;
      case 'currency': options = number.currency(options); break;
      case 'date': [elem, options] = date(elem, options); break;
      case 'email': [elem, options] = email(elem, options); break;
      case 'hidden': options.type = 'hidden'; break;
      case 'integer': options = number.integer(options); break;
      case 'float': options = number.float(options); break;
      case 'password': [elem, options] = password(elem, options); break;
      case 'phone': {
        const { phone } = await import('./phone');
        [elem, options] = await phone(elem, options);
        break;
      }
      case 'radio':
        options.type = 'radio';
        options.text = options.text || '';
        switch (options.appearance) {
          case 'switch': options.class = 'switch'; break;
          case 'button': options.class = 'ch-button'; break;
        }
        break;
      case 'search': options.type = 'search'; break;
      case 'slider': options = slider(options); break;
      case 'spinner': [elem, options] = spinner(elem, options); break;
      case 'time': [elem, options] = time(elem, options); break;
      case 'url': [elem, options] = url(elem, options); break;
      default:
        options.type = 'text'; break;
    }

    return this.#createInputOptions(elem, options);
  }
}

const createInput = new Input();

/**
 * Factory comum dos elementos baseados em `<input>`.
 *
 * @param {Object} options Configuração recebida pelo pipeline; veja {@link Input#input}.
 * @returns {Promise.<Array.<HTMLInputElement|Object>>} Input e opções finais.
 * @see Input#input
 * @see module:components/create/element
 */
export default (options) => createInput.input(options);
